import { useEffect, useState } from 'react'

import { useServerTimeOffsetMs } from '~/hooks/useServerTimeOffsetMs'
import { now as serverNow } from '~/models/clock'

/**
 * JST の 0:00 (日付境界) をまたぐタイミングで 1 回だけ tick を進めて呼び出し元を
 * 再レンダーさせる hook。境界通過後にすぐ次の 00:00 を予約し直す (毎日 1 回)。
 *
 * 用途 (PR #5066 review C-7): 需要想定 daily 画面の過去日 gate は render 時計算
 * のため、画面を開いたまま JST 0:00 を跨ぐと `canMutateTargetDate` が古い
 * true のまま残り、KomaGrid/保存ボタン/各種操作の disabled 表示が当日状態のまま
 * になる。書き込みハンドラは `canMutateNow()` (models/canMutate) で実時刻 guard
 * するが、ユーザーには「入力できたように見えて反映されない」silent 破棄に見える
 * (表示と実行の乖離)。
 * 本 hook で日跨ぎ直後に 1 回だけ再レンダーを起こし、gate と表示を過去日状態へ
 * 即時切り替える。
 *
 * setInterval で毎分回すのはページ全体の render コストが大きいため採用しない
 * (JST 日付境界という業務ロジックの節目に合わせた setTimeout 1 発方式)。
 * `+1s` のマージンで境界越えを保証する。
 */
export function useJstMidnightTick(): number {
  const [tick, setTick] = useState(0)
  // server time offset は session RPC 完了時に確定しうる。 予約済みの次回 0:00 は
  // ブラウザ時計基準のままなので、 offset 確定時に予約し直す。
  const offsetMs = useServerTimeOffsetMs()
  useEffect(() => {
    let timerId: ReturnType<typeof setTimeout>
    const schedule = (): void => {
      const nowMs = serverNow().getTime()
      const jstOffsetMs = 9 * 60 * 60 * 1000
      const nowJst = nowMs + jstOffsetMs
      const msPerDay = 24 * 60 * 60 * 1000
      const msUntilNextJstMidnight = msPerDay - (nowJst % msPerDay) + 1000
      timerId = setTimeout(() => {
        setTick((n) => n + 1)
        schedule()
      }, msUntilNextJstMidnight)
    }
    schedule()
    return () => {
      clearTimeout(timerId)
    }
  }, [offsetMs])
  // consumer は戻り値を無視してよい (呼ぶだけで re-render 誘発の副作用を得る)。
  // ただし React の state 変更が呼び出し元に伝播することを表現するため戻り値を
  // 露出しておく (test で `result.current` を観測できるようにする副次目的も兼ねる)。
  return tick
}
